import { useQuery } from '@tanstack/react-query';
import { LocationsApi } from '@up2stay/up2promo-api-client';

import {
  LIST_PAGE_HARD_CAP,
  shouldRetryQuery,
} from '@/core/lib/list-pagination';
import { locationsQueryKeys } from '@/core/query-keys/locations-query-keys';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

export type UseLocationsOptions = {
  /** When false, the query will not run. Defaults to true when workspaceId is set. */
  enabled?: boolean;
  /** Max number of locations to fetch. Default / hard-capped at 100 (API clamp). */
  limit?: number;
  /** Page for pagination. Default 1. */
  page?: number;
  /** Optional filters (e.g. search). Passed as JSON string to API. */
  filters?: string;
  /** Stale time in ms. Default 60_000. */
  staleTime?: number;
  /** Optional suffix for queryKey to separate cache entries (e.g. 'profile-module'). */
  queryKeySuffix?: string;
};

/**
 * Fetches locations for the current workspace (or the given workspaceId).
 * Query key includes page/limit/filters. Prefix invalidation via
 * `invalidateQueries({ queryKey: locationsQueryKeys.root })` still refreshes all consumers.
 */
export function useLocations(
  workspaceId?: string | null,
  options: UseLocationsOptions = {},
) {
  const currentWorkspace = useCurrentWorkspace();
  const effectiveWorkspaceId = workspaceId ?? currentWorkspace?.id;

  const {
    enabled = true,
    limit: requestedLimit = LIST_PAGE_HARD_CAP,
    page = 1,
    filters,
    staleTime = 60 * 1000,
    queryKeySuffix,
  } = options;
  const limit = Math.min(Math.max(1, requestedLimit), LIST_PAGE_HARD_CAP);

  // Include page/limit/filters in the key. Callers use different limits (e.g.
  // get-started uses limit: 1); sharing only workspaceId would return a
  // truncated list to pages that requested more.
  const queryKey = locationsQueryKeys.list(
    effectiveWorkspaceId,
    page,
    limit,
    filters ?? null,
    queryKeySuffix,
  );

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!effectiveWorkspaceId) {
        return { data: [], totalPages: 1, hasNextPage: false };
      }
      const res = await LocationsApi.findAll({
        path: { workspaceId: effectiveWorkspaceId },
        query: {
          page,
          limit,
          ...(filters ? { filters } : {}),
        },
      });
      return {
        data: res.data?.data ?? [],
        totalPages: res.data?.totalPages ?? 1,
        hasNextPage: res.data?.hasNextPage ?? false,
      };
    },
    enabled: enabled && !!effectiveWorkspaceId,
    staleTime,
    retry: shouldRetryQuery,
  });

  const locations = query.data?.data ?? [];

  return {
    ...query,
    locations,
  };
}
